<template>
  <div ref="botRef" class="feedback-bot" :style="botStyle">
    <!-- Dialog Popup -->
    <Transition name="bot-popup">
      <div v-if="dialogVisible" class="bot-dialog" :style="dialogStyle">
        <!-- Header -->
        <div class="bot-dialog-header">
          <span class="bot-dialog-title">
            <span class="title-emoji">{{
              activeMode === 'wishlist' ? '🌟' : '🐛'
            }}</span>
            {{ t(`components.feedbackBot.${activeMode}.title`) }}
          </span>
          <button class="bot-close-btn" @click="closeDialog">
            <IconClose :size="14" />
          </button>
        </div>

        <!-- Mode Tabs -->
        <div class="bot-mode-tabs">
          <button
            :class="[
              'bot-mode-tab',
              { 'bot-mode-tab--active': activeMode === 'wishlist' },
            ]"
            @click="switchMode('wishlist')"
          >
            {{ t('components.feedbackBot.wishlist.tab') }}
          </button>
          <button
            :class="[
              'bot-mode-tab',
              { 'bot-mode-tab--active': activeMode === 'feedback' },
            ]"
            @click="switchMode('feedback')"
          >
            {{ t('components.feedbackBot.feedback.tab') }}
          </button>
        </div>

        <!-- Content: success or form -->
        <Transition name="bot-fade" mode="out-in">
          <div v-if="submitted" key="success" class="bot-success">
            <div class="bot-success-emoji">🎉</div>
            <p class="bot-success-text">
              {{ t(`components.feedbackBot.${activeMode}.success`) }}
            </p>
          </div>

          <a-form
            v-else
            key="form"
            ref="formRef"
            :model="form"
            layout="vertical"
            class="bot-form"
          >
            <a-form-item
              v-if="activeMode === 'feedback'"
              field="type"
              :label="t('components.feedbackBot.feedback.typeLabel')"
              :rules="[
                {
                  required: true,
                  message: t('components.feedbackBot.required'),
                },
              ]"
            >
              <a-select
                v-model="form.type"
                :placeholder="
                  t('components.feedbackBot.feedback.typePlaceholder')
                "
              >
                <a-option value="bug">{{
                  t('components.feedbackBot.feedback.types.bug')
                }}</a-option>
                <a-option value="ui">{{
                  t('components.feedbackBot.feedback.types.ui')
                }}</a-option>
                <a-option value="performance">
                  {{ t('components.feedbackBot.feedback.types.performance') }}
                </a-option>
                <a-option value="other">
                  {{ t('components.feedbackBot.feedback.types.other') }}
                </a-option>
              </a-select>
            </a-form-item>

            <a-form-item
              field="content"
              :label="t(`components.feedbackBot.${activeMode}.contentLabel`)"
              :rules="[
                {
                  required: true,
                  message: t('components.feedbackBot.required'),
                },
              ]"
            >
              <a-textarea
                v-model="form.content"
                :placeholder="
                  t(`components.feedbackBot.${activeMode}.contentPlaceholder`)
                "
                :auto-size="{ minRows: 3, maxRows: 5 }"
              />
            </a-form-item>

            <a-form-item
              field="contact"
              :label="t(`components.feedbackBot.${activeMode}.contactLabel`)"
            >
              <a-input
                v-model="form.contact"
                :placeholder="
                  t(`components.feedbackBot.${activeMode}.contactPlaceholder`)
                "
              />
            </a-form-item>

            <div class="bot-form-footer">
              <a-button
                type="primary"
                :loading="loading"
                long
                @click="handleSubmit"
              >
                {{ t(`components.feedbackBot.${activeMode}.submit`) }}
              </a-button>
            </div>
          </a-form>
        </Transition>
      </div>
    </Transition>

    <!-- Robot Button -->
    <button
      class="bot-btn"
      :class="{ 'bot-btn--open': dialogVisible }"
      @mousedown="onBtnMousedown"
      @click="toggleDialog"
    >
      <span v-if="!dialogVisible" class="pulse-ring" />
      <div class="bot-icon">
        <svg
          class="bot-robot"
          viewBox="0 0 44 46"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          aria-hidden="true"
        >
          <!-- Antenna stem -->
          <line
            x1="22"
            y1="4"
            x2="22"
            y2="11"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            opacity="0.8"
          />
          <!-- Antenna ball -->
          <circle cx="22" cy="3" r="2.8" fill="white" opacity="0.8" />
          <!-- Head -->
          <rect x="5" y="11" width="34" height="24" rx="8" fill="white" />
          <!-- Left eye -->
          <g class="bot-eye">
            <circle cx="16" cy="22" r="4.5" class="bot-eye-socket" />
            <circle cx="16.8" cy="22.6" r="2" fill="white" />
            <circle cx="15.2" cy="20.8" r="0.7" fill="white" opacity="0.6" />
          </g>
          <!-- Right eye -->
          <g class="bot-eye bot-eye-r">
            <circle cx="28" cy="22" r="4.5" class="bot-eye-socket" />
            <circle cx="28.8" cy="22.6" r="2" fill="white" />
            <circle cx="27.2" cy="20.8" r="0.7" fill="white" opacity="0.6" />
          </g>
          <!-- Smile -->
          <path
            class="bot-mouth"
            d="M15 29 Q22 33.5 29 29"
            stroke-width="1.8"
            stroke-linecap="round"
            fill="none"
          />
          <!-- Body -->
          <rect
            x="15"
            y="36"
            width="14"
            height="8"
            rx="4"
            fill="white"
            opacity="0.8"
          />
          <!-- Left arm -->
          <circle cx="8" cy="40" r="4.5" fill="white" opacity="0.65" />
          <!-- Right arm -->
          <circle cx="36" cy="40" r="4.5" fill="white" opacity="0.65" />
        </svg>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
  import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { IconClose } from '@arco-design/web-vue/es/icon';
  import { createWishlistApi, createFeedbackApi } from '@/api/feedback';
  import type { FeedbackIssueType } from '@/types/feedback';

  type Mode = 'wishlist' | 'feedback';

  interface FeedbackForm {
    content: string;
    contact: string;
    type: string;
  }

  const BTN_SIZE = 56;
  const STORAGE_KEY = 'feedback-bot-pos';
  const DRAG_THRESHOLD = 5;

  const { t } = useI18n();

  const botRef = ref<HTMLElement | null>(null);
  const formRef = ref();
  const dialogVisible = ref(false);
  const activeMode = ref<Mode>('wishlist');
  const loading = ref(false);
  const submitted = ref(false);

  // ---- Drag state ----
  const pos = reactive({ x: 0, y: 0 });
  const isDragging = ref(false);
  const hasDragged = ref(false);
  const dragOrigin = { mouseX: 0, mouseY: 0, posX: 0, posY: 0 };

  const clampPos = (val: number, min: number, max: number) =>
    Math.max(min, Math.min(max, val));

  const botStyle = computed(() => ({
    transform: `translate(${pos.x}px, ${pos.y}px)`,
  }));

  const dialogStyle = computed(() => {
    const isRightHalf = pos.x + BTN_SIZE / 2 > window.innerWidth / 2;
    const isBottomHalf = pos.y + BTN_SIZE / 2 > window.innerHeight / 2;
    return {
      [isRightHalf ? 'right' : 'left']: '0px',
      [isBottomHalf ? 'bottom' : 'top']: 'calc(100% + 12px)',
    };
  });

  // ---- Form state ----
  const form = reactive<FeedbackForm>({
    content: '',
    contact: '',
    type: '',
  });

  const resetForm = () => {
    form.content = '';
    form.contact = '';
    form.type = '';
    submitted.value = false;
    formRef.value?.clearValidate();
  };

  const closeDialog = () => {
    dialogVisible.value = false;
    resetForm();
  };

  const toggleDialog = () => {
    if (hasDragged.value) return;
    if (dialogVisible.value) {
      closeDialog();
    } else {
      dialogVisible.value = true;
    }
  };

  const switchMode = (mode: Mode) => {
    if (activeMode.value === mode) return;
    activeMode.value = mode;
    resetForm();
  };

  const handleSubmit = async () => {
    const err = await formRef.value?.validate();
    if (err) return;

    loading.value = true;
    try {
      if (activeMode.value === 'wishlist') {
        await createWishlistApi({
          content: form.content,
          contact: form.contact || undefined,
        });
      } else {
        await createFeedbackApi({
          issue_type: form.type as FeedbackIssueType,
          content: form.content,
          contact: form.contact || undefined,
        });
      }
      submitted.value = true;
      setTimeout(() => {
        submitted.value = false;
        resetForm();
      }, 3000);
    } finally {
      loading.value = false;
    }
  };

  // ---- Drag handlers ----
  const onDragMove = (e: MouseEvent) => {
    const dx = e.clientX - dragOrigin.mouseX;
    const dy = e.clientY - dragOrigin.mouseY;

    if (!isDragging.value && Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
      isDragging.value = true;
      hasDragged.value = true;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      closeDialog();
    }

    if (isDragging.value) {
      pos.x = clampPos(dragOrigin.posX + dx, 0, window.innerWidth - BTN_SIZE);
      pos.y = clampPos(dragOrigin.posY + dy, 0, window.innerHeight - BTN_SIZE);
    }
  };

  const onDragEnd = () => {
    isDragging.value = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ x: pos.x, y: pos.y }));
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
  };

  const onBtnMousedown = (e: MouseEvent) => {
    e.preventDefault();
    hasDragged.value = false;
    dragOrigin.mouseX = e.clientX;
    dragOrigin.mouseY = e.clientY;
    dragOrigin.posX = pos.x;
    dragOrigin.posY = pos.y;
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
  };

  // ---- Click-outside ----
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (
      dialogVisible.value &&
      botRef.value &&
      !botRef.value.contains(target) &&
      !(target as Element).closest?.('.arco-select-dropdown')
    ) {
      closeDialog();
    }
  };

  // ---- Window resize ----
  const onWindowResize = () => {
    pos.x = clampPos(pos.x, 0, window.innerWidth - BTN_SIZE);
    pos.y = clampPos(pos.y, 0, window.innerHeight - BTN_SIZE);
  };

  onMounted(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { x, y } = JSON.parse(saved);
        pos.x = clampPos(x, 0, window.innerWidth - BTN_SIZE);
        pos.y = clampPos(y, 0, window.innerHeight - BTN_SIZE);
      } catch {
        pos.x = window.innerWidth - BTN_SIZE - 32;
        pos.y = window.innerHeight - BTN_SIZE - 32;
      }
    } else {
      pos.x = window.innerWidth - BTN_SIZE - 32;
      pos.y = window.innerHeight - BTN_SIZE - 32;
    }
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', onWindowResize);
  });

  onUnmounted(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    window.removeEventListener('resize', onWindowResize);
  });
</script>

<style lang="less" scoped>
  /* -------- Layout -------- */
  .feedback-bot {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    will-change: transform;
  }

  /* -------- Dialog -------- */
  .bot-dialog {
    position: absolute;
    width: 320px;
    background: var(--color-bg-2);
    border: 1px solid var(--color-border-2);
    border-radius: 16px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  .bot-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 16px 0;
  }

  .bot-dialog-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 600;
    color: var(--color-text-1);

    .title-emoji {
      font-size: 16px;
      line-height: 1;
    }
  }

  .bot-close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: none;
    background: transparent;
    border-radius: 50%;
    cursor: pointer;
    color: var(--color-text-3);
    transition: background 0.2s, color 0.2s;
    padding: 0;
    flex-shrink: 0;

    &:hover {
      background: var(--color-fill-2);
      color: var(--color-text-1);
    }
  }

  /* -------- Mode Tabs -------- */
  .bot-mode-tabs {
    display: flex;
    gap: 6px;
    padding: 12px 16px 0;
  }

  .bot-mode-tab {
    flex: 1;
    padding: 6px 0;
    border: 1px solid var(--color-border-2);
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    color: var(--color-text-2);
    transition: border-color 0.2s, color 0.2s, background 0.2s;
    white-space: nowrap;

    &:hover {
      border-color: rgb(var(--primary-6));
      color: rgb(var(--primary-6));
    }

    &--active {
      border-color: rgb(var(--primary-6));
      background: rgb(var(--primary-1));
      color: rgb(var(--primary-6));
      font-weight: 500;
    }
  }

  /* -------- Form -------- */
  .bot-form {
    padding: 12px 16px 16px;

    :deep(.arco-form-item) {
      margin-bottom: 12px;
    }

    :deep(.arco-form-item-label) {
      font-size: 13px;
    }
  }

  .bot-form-footer {
    margin-top: 4px;
  }

  /* -------- Success State -------- */
  .bot-success {
    padding: 28px 16px 24px;
    text-align: center;

    .bot-success-emoji {
      font-size: 40px;
      margin-bottom: 12px;
      animation: bot-bounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .bot-success-text {
      font-size: 14px;
      color: var(--color-text-2);
      line-height: 1.6;
      margin: 0;
    }
  }

  /* -------- Robot Button -------- */
  .bot-btn {
    position: relative;
    width: 56px;
    height: 56px;
    border: none;
    border-radius: 50%;
    background: rgb(var(--primary-6));
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
      filter 0.2s ease, box-shadow 0.2s ease;
    outline: none;
    padding: 0;
    cursor: grab;

    &:hover {
      transform: scale(1.1);
      filter: brightness(1.08);
    }

    &:active {
      transform: scale(0.93);
    }

    &--open {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

      .bot-icon {
        animation-play-state: paused;
      }

      &:hover {
        transform: scale(1.03);
        filter: brightness(1.05);
      }
    }
  }

  /* -------- Pulse Ring -------- */
  .pulse-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgb(var(--primary-6));
    animation: bot-pulse 2.4s ease-out infinite;
    pointer-events: none;
  }

  /* -------- Robot Icon -------- */
  .bot-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    animation: bot-float 3.2s ease-in-out infinite;
    position: relative;
    z-index: 1;
  }

  /* -------- SVG-specific Styles -------- */
  .bot-eye {
    transform-box: fill-box;
    transform-origin: center;
    animation: bot-blink 4.5s ease-in-out infinite;
  }

  .bot-eye-r {
    animation-delay: 0.06s;
  }

  .bot-eye-socket {
    fill: rgb(var(--primary-6));
  }

  .bot-mouth {
    stroke: rgb(var(--primary-6));
  }

  /* -------- Dialog Transition -------- */
  .bot-popup-enter-active,
  .bot-popup-leave-active {
    transition: opacity 0.25s ease,
      transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .bot-popup-enter-from,
  .bot-popup-leave-to {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }

  /* -------- Form/Success Fade Transition -------- */
  .bot-fade-enter-active,
  .bot-fade-leave-active {
    transition: opacity 0.16s ease;
  }

  .bot-fade-enter-from,
  .bot-fade-leave-to {
    opacity: 0;
  }

  /* -------- Keyframe Animations -------- */
  @keyframes bot-float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-4px);
    }
  }

  @keyframes bot-blink {
    0%,
    86%,
    100% {
      transform: scaleY(1);
    }
    90%,
    94% {
      transform: scaleY(0.07);
    }
  }

  @keyframes bot-pulse {
    0% {
      transform: scale(1);
      opacity: 0.5;
    }
    100% {
      transform: scale(1.8);
      opacity: 0;
    }
  }

  @keyframes bot-bounce {
    0% {
      transform: scale(0.5);
    }
    60% {
      transform: scale(1.15);
    }
    80% {
      transform: scale(0.95);
    }
    100% {
      transform: scale(1);
    }
  }
</style>
